import React, { useState, useEffect } from "react";
import { ShieldAlert, ShieldCheck, Download, AlertTriangle, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_CONFIG = {
  login_success: { label: "Connexion réussie", icon: ShieldCheck, color: "text-green-600 bg-green-50" },
  login_failed: { label: "Connexion échouée", icon: AlertTriangle, color: "text-orange-600 bg-orange-50" },
  brute_force_blocked: { label: "IP bloquée", icon: ShieldAlert, color: "text-red-600 bg-red-50" },
};

export const SecurityLogsSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/config/security/logs`);
        setData(res.data);
      } catch { /* */ }
      setLoading(false);
    })();
  }, []);

  const downloadBackup = async () => {
    try {
      const res = await axios.get(`${API}/admin/config/backup`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `lead_artisan_backup_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { /* */ }
  };

  const importBackup = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm("Importer ce backup va REMPLACER toutes les données actuelles. Continuer ?")) return;
    try {
      const text = await file.text();
      await axios.post(`${API}/admin/config/backup/import`, JSON.parse(text), { headers: { "Content-Type": "application/json" } });
      alert("Backup importé avec succès !");
      window.location.reload();
    } catch (err) {
      alert("Erreur d'import : " + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border p-6">
        <h2 className="text-lg font-bold mb-4 flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-primary" /> Sécurité</h2>
        <div className="flex justify-center py-8"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>
      </div>
    );
  }

  if (!data) return null;
  const { logs, stats } = data;

  return (
    <div className="bg-card rounded-lg border p-6" data-testid="security-logs">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-primary" /> Sécurité & Backup
        </h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={downloadBackup}>
            <Download className="w-4 h-4 mr-1" /> Backup
          </Button>
          <label className="cursor-pointer">
            <input type="file" accept=".json" onChange={importBackup} className="hidden" />
            <span className="inline-flex items-center gap-1 border rounded-md px-3 py-1.5 text-xs font-medium hover:bg-muted/50 transition-colors">
              <Upload className="w-4 h-4" /> Import
            </span>
          </label>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <p className="text-xl font-black text-green-700">{stats.success_24h}</p>
          <p className="text-[10px] text-green-600">Connexions 24h</p>
        </div>
        <div className="bg-orange-50 rounded-lg p-3 text-center">
          <p className="text-xl font-black text-orange-700">{stats.failed_24h}</p>
          <p className="text-[10px] text-orange-600">Échecs 24h</p>
        </div>
        <div className="bg-red-50 rounded-lg p-3 text-center">
          <p className="text-xl font-black text-red-700">{stats.blocked_7d}</p>
          <p className="text-[10px] text-red-600">IPs bloquées 7j</p>
        </div>
      </div>

      {/* Top failed IPs */}
      {stats.top_failed_ips?.length > 0 && (
        <div className="mb-5">
          <p className="text-xs font-semibold mb-2">IPs suspectes (7 jours)</p>
          <div className="space-y-1">
            {stats.top_failed_ips.map((item, i) => (
              <div key={i} className="flex items-center justify-between text-xs bg-red-50/50 rounded px-3 py-1.5">
                <span className="font-mono text-muted-foreground">{item.ip}</span>
                <span className="font-bold text-red-600">{item.attempts} tentatives</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent logs */}
      <details>
        <summary className="text-sm font-semibold cursor-pointer mb-2">Journal récent ({logs.length})</summary>
        <div className="max-h-64 overflow-y-auto space-y-1">
          {logs.map((log, i) => {
            const cfg = ACTION_CONFIG[log.action] || ACTION_CONFIG.login_failed;
            const Icon = cfg.icon;
            const ts = log.ts ? new Date(log.ts).toLocaleString("fr-CH") : "";
            return (
              <div key={i} className="flex items-center gap-2 text-xs py-1.5 px-2 rounded bg-muted/20">
                <span className={`px-1.5 py-0.5 rounded font-medium ${cfg.color}`}>{cfg.label}</span>
                <span className="text-muted-foreground font-mono">{log.ip}</span>
                {log.username && <span className="text-muted-foreground">{log.username}</span>}
                <span className="ml-auto text-muted-foreground text-[10px]">{ts}</span>
              </div>
            );
          })}
        </div>
      </details>
    </div>
  );
};
